import { useBackendApi } from './backendApi';
import { useEventsPublisher } from 'event-bus';
import { ResourceNotFoundError } from 'request-sender';
import { useObjectDatesMapper } from './Helper';

export enum SubscriptionEvent {
  TRANSACTION_LAUNCHED = 'TRANSACTION_LAUNCHED',
  TRANSACTION_UPDATED = 'TRANSACTION_UPDATED',
  CONTACT_CREATED = 'CONTACT_CREATED',
  CONTACT_UPDATED = 'CONTACT_UPDATED',
  CONTACT_DELETED = 'CONTACT_DELETED',

  EMAIL_SENT = 'EMAIL_SENT',
  SMS_SENT = 'SMS_SENT',

  TRANSACTION_NOTE_CREATED = 'TRANSACTION_NOTE_CREATED',
  TRANSACTION_NOTE_UPDATED = 'TRANSACTION_NOTE_UPDATED',
  TRANSACTION_NOTE_DELETED = 'TRANSACTION_NOTE_DELETED',

  TRANSACTION_ASSIGNMENT_CREATED = 'TRANSACTION_ASSIGNMENT_CREATED',
  TRANSACTION_ASSIGNMENT_DELETED = 'TRANSACTION_ASSIGNMENT_DELETED',

  TRANSACTION_TYPE_UPDATED = 'TRANSACTION_TYPE_UPDATED',
  TRANSACTION_STATUS_UPDATED = 'TRANSACTION_STATUS_UPDATED',

  CONTACT_NOTE_CREATED = 'CONTACT_NOTE_CREATED',
  CONTACT_NOTE_UPDATED = 'CONTACT_NOTE_UPDATED',
  CONTACT_NOTE_DELETED = 'CONTACT_NOTE_DELETED',
}

export interface CreateWebhookSubscriptionRequest {
  webhookUrl: string;
  eventType: SubscriptionEvent;
}

export interface WebhookSubscription {
  id: number;
  webhookUrl: string;
  eventType: SubscriptionEvent;
  isEnabled: boolean;
  totalNumberOfSuccessRequests: number;
  totalNumberOfErrorRequests: number;
  currentErrorRequests: number;
  isCreatedViaApi: boolean;
  createdAt: Date;
}

export type WebhookSubscriptionsFilter = {
  event?: SubscriptionEvent;
  onlyManuallyCreated?: boolean;
}

export const SUBSCRIPTIONS_UPDATED = 'SUBSCRIPTIONS_UPDATED';

export function useWebhookSubscriptionsService() {
  const nekstApi = useBackendApi();
  const eventsPublisher = useEventsPublisher();

  const objectMapper = useObjectDatesMapper<WebhookSubscription>(['createdAt'], []);

  const createSubscription = async (request: CreateWebhookSubscriptionRequest): Promise<WebhookSubscription> => {
    const response = await nekstApi.post('/integrations/subscriptions', request) as WebhookSubscription;
    eventsPublisher.publish(SUBSCRIPTIONS_UPDATED, {});
    return objectMapper.mapSingle(response);
  };

  const getSubscriptions = async (filter?: WebhookSubscriptionsFilter): Promise<WebhookSubscription[]> => {
    const event = filter?.event;

    const query = event ? '?event=' + event : '';

    let result =  objectMapper.mapList(await nekstApi.get('/integrations/subscriptions' + query));

    if (filter?.onlyManuallyCreated) {
      result = result.filter((item) => !item.isCreatedViaApi)
    }

    return result;
  };

  const getSubscriptionById = async (subscriptionId: number): Promise<WebhookSubscription> => {
    const allSubscriptions = await getSubscriptions();
    const result = allSubscriptions.find(subscription => subscription.id === subscriptionId);

    if (!result) {
      throw new ResourceNotFoundError('Subscription not found');
    }

    return objectMapper.mapSingle(result);
  }

  const cancelSubscription = async (subscriptionId: number): Promise<void> => {
    await nekstApi.delete(`/integrations/subscriptions/${subscriptionId}`);
    eventsPublisher.publish(SUBSCRIPTIONS_UPDATED, {});
  };

  const enableSubscription = async (subscriptionId: number): Promise<WebhookSubscription> => {
    return objectMapper.mapSingle(await nekstApi.put(`/integrations/subscriptions/${subscriptionId}/enable`));
  }

  return {
    createSubscription,
    getSubscriptions,
    getSubscriptionById,
    cancelSubscription,
    enableSubscription,
  };
}
